import { translate } from '../../../i18n';
import { GameDurationPricingImpactDto } from '../../../models/organization/dto/organization-sport-game-duration.dto';

/**
 * The two ways a game-duration write can leave the organization worse off than
 * the green toast suggests. Both are additive, optional fields on the
 * create/update/delete responses; an API that predates them sends nothing,
 * which reads here as "no warning".
 *
 * Deliberately a sibling of `game-capacity-management/utils/capacity-write-warnings.ts`
 * rather than a shared abstraction: the two axes report different things
 * (a duration is one number, capacities are a list) and the capacity file's
 * three fields do not generalise. What IS shared is the rule — warn, never
 * block, and join sentences into one toast.
 */
export type DurationWriteWarningSource = GameDurationPricingImpactDto;

/**
 * Builds the operator-facing warning for one game-duration write, or `null`
 * when there is nothing to say.
 *
 * `unpricedDurationMinutes` is the sharp one and the reason this file exists.
 * There is no wildcard row on the duration axis — the API matches
 * `slotDurationInMs` exactly — so offering a duration nothing prices does not
 * fall back to another price, it returns zero courts. Combined with the mobile
 * booking screen opening at 60 minutes and only snapping away from a value that
 * is NOT offered, adding a 60-minute duration to a venue that prices only
 * 90-minute slots takes the whole sport offline for every player. The write
 * itself is legitimate and succeeds; what the operator gets back is the step
 * that is still missing.
 */
export function buildDurationWriteWarning(
  result: DurationWriteWarningSource | undefined | null,
): string | null {
  if (!result) {
    return null;
  }

  const sentences: string[] = [];

  const unpriced = result.unpricedDurationMinutes;
  if (unpriced !== undefined && unpriced !== null) {
    sentences.push(
      translate('game_durations.unpriced_duration').replace(
        /\{\{duration\}\}/g,
        String(unpriced)
      )
    );
  }

  const stranded = result.strandedPricingRows ?? 0;
  if (stranded > 0) {
    sentences.push(
      translate('game_durations.stranded_pricing_rows').replace(
        '{{count}}',
        String(stranded)
      )
    );
  }

  return sentences.length > 0 ? sentences.join(' ') : null;
}
